/** Emailing reports: schedules, Send now and approvals. Shapes mirror apps/api/src/schedule-routes.ts. */

import { apiCall, orgPath } from '../../api.ts';
import type { RangeSetting } from '../reports-api.ts';

export type Frequency = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';

export interface EmailFields {
  recipients: string[];
  subject: string;
  message: string;
}

export interface ScheduleInput extends EmailFields {
  frequency: Frequency;
  /** Only the fields the frequency uses are read; the rest may be empty. */
  day_of_week: number | null;
  day_of_month: number | null;
  month: number | null;
  send_time: string;
  skip_weekends: boolean;
  require_approval: boolean;
  allow_range_change: boolean;
  active: boolean;
}

export interface Schedule extends ScheduleInput {
  id: string;
  timezone: string;
  next_run_at: string | null;
}

export interface ReportSend extends EmailFields {
  id: string;
  schedule_id: string | null;
  status: 'AWAITING_APPROVAL' | 'SENDING' | 'SENT' | 'REJECTED' | 'FAILED';
  error: string | null;
  created_at: string;
  sent_at: string | null;
}

const reports = (org: string, path: string) => orgPath(org, `/reports${path}`);
const send = <T>(path: string, method: string, body?: unknown) =>
  apiCall<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

export const emailApi = {
  schedules: (org: string, documentId: string) =>
    apiCall<{ schedules: Schedule[] }>(reports(org, `/documents/${documentId}/schedules`)).then(
      (data) => data.schedules,
    ),
  createSchedule: (org: string, documentId: string, input: ScheduleInput) =>
    send<{ schedule: Schedule }>(reports(org, `/documents/${documentId}/schedules`), 'POST', input),
  updateSchedule: (org: string, id: string, input: ScheduleInput) =>
    send<{ schedule: Schedule }>(reports(org, `/schedules/${id}`), 'PUT', input),
  deleteSchedule: (org: string, id: string) =>
    send<{ deleted: boolean }>(reports(org, `/schedules/${id}`), 'DELETE'),
  sends: (org: string, documentId: string) =>
    apiCall<{ sends: ReportSend[] }>(reports(org, `/documents/${documentId}/sends`)).then(
      (data) => data.sends,
    ),
  sendNow: (
    org: string,
    documentId: string,
    input: EmailFields & { range: RangeSetting; allow_range_change: boolean },
  ) => send<{ send: ReportSend }>(reports(org, `/documents/${documentId}/sends`), 'POST', input),
  decide: (org: string, id: string, approve: boolean) =>
    send<{ send: ReportSend }>(
      reports(org, `/sends/${id}/${approve ? 'approve' : 'reject'}`),
      'POST',
    ),
};
